import React, { useCallback, useEffect, useState } from 'react';
import RangeIcon from '/public/range.svg';
import { Box, Typography } from '@mui/material';

import { InlineValue } from '../../components/InlineValue';
import { IEnrichedUniswapPosition } from '../../types/uniswapTokens';
import { displayTokenAmount } from '../../utils/displayTokenAmount';
import { CurrencyToggler } from '../../components-yldr/components/CurrencyToggler';
import { useCurrency } from '../../components-yldr/LiquidityChartRangeInput/hooks/useCurrency';
import { findTokenInReserves } from '../../utils/findTokenInReserves';
import { ComputedReserveData, useAppDataContext } from '../../hooks/app-data-provider/useAppDataProvider';
import { getTickToPrice } from '../../components-yldr/LiquidityChartRangeInput/utils/getTickToPrice';

export const LeveragePositionRange = ({
  poolPrice,
  invertedPoolPrice,
  token0,
  token1,
  tickLower,
  tickUpper,
}: IEnrichedUniswapPosition) => {
  const [tokens, setTokens] = useState<{ tokenA?: ComputedReserveData;  tokenB?: ComputedReserveData }>();
  const { reserves } = useAppDataContext();

  const baseCurrency = useCurrency(tokens?.tokenA);
  const quoteCurrency = useCurrency(tokens?.tokenB);

  const isSorted = baseCurrency && quoteCurrency && baseCurrency?.wrapped.sortsBefore(quoteCurrency?.wrapped);
  const lowerPrice = getTickToPrice(baseCurrency?.wrapped, quoteCurrency?.wrapped, isSorted ? tickLower : tickUpper);
  const upperPrice = getTickToPrice(baseCurrency?.wrapped, quoteCurrency?.wrapped, isSorted ? tickUpper : tickLower);
  const currentPrice = isSorted ? poolPrice : invertedPoolPrice;

  const handleOnCurrencyToggle = useCallback(() => {
    setTokens({
      tokenA: tokens?.tokenB,
      tokenB: tokens?.tokenA,
    });
  }, [tokens]);

  useEffect(() => {
    const tokenA = findTokenInReserves({ reserves, address: token0.underlyingAsset });
    const tokenB = findTokenInReserves({ reserves, address: token1.underlyingAsset });
    setTokens({ tokenA, tokenB });
  }, [token0.underlyingAsset, token1.underlyingAsset]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="h4">Price Range</Typography>
          <InlineValue label="Current Price:" value={displayTokenAmount(currentPrice)} />
        </Box>
        <CurrencyToggler
          baseCurrencySymbol={isSorted ? token0.symbol : token1.symbol}
          quoteCurrencySymbol={isSorted ? token1.symbol : token0.symbol}
          isSorted={isSorted}
          onCurrencyToggle={handleOnCurrencyToggle}
        />
      </Box>
      <Box
        sx={{ display: 'flex', gap: 3, my: 2 }}
      >
        <RangeLabel
          title="Min Price:"
          value={displayTokenAmount(lowerPrice?.toFixed(12))}
          description={`${tokens?.tokenA?.symbol} per ${tokens?.tokenB?.symbol}`}
        />
        <RangeIcon />
        <RangeLabel
          title="Max Price:"
          value={displayTokenAmount(upperPrice?.toFixed(12))}
          description={`${tokens?.tokenA?.symbol} per ${tokens?.tokenB?.symbol}`}
        />
      </Box>
    </Box>
  );
};

export const RangeLabel = ({ title, value, description }: { title: string; value: string; description: string; }) => (
  <Box
    sx={{
      px: 4,
      py: 3,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      alignItems: 'center',
      justifyContent: 'center',
      flex: '1 1 auto',
      borderRadius: 3,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="main13" color="text.secondary">{title}</Typography>
    <Typography sx={{ fontSize: '15px', fontWeight: '700' }}>
      {value}
    </Typography>
    <Typography variant="main12" color="text.tertiary">
      {description}
    </Typography>
  </Box>
)
